'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiDialogContentText, {
  type DialogContentTextTypeMap as MuiDialogContentTextTypeMap,
} from '@mui/material/DialogContentText';
import { useTheme } from '@mui/material/styles';

type ExcludedMuiProps = '';

type CustomProps = {
  component?: ElementType;
};

type DialogContentTextTypeMap<
  P = CustomProps,
  D extends ElementType = MuiDialogContentTextTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiDialogContentTextTypeMap['props'], ExcludedMuiProps> & P;
};

export type DialogContentTextProps<
  D extends ElementType = MuiDialogContentTextTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiDialogContentTextTypeMap<P, D>, D>;

const DialogContentTextComponent = (
  props: DialogContentTextProps,
  ref: Ref<HTMLSpanElement>
) => {
  const theme = useTheme();
  return (
    <MuiDialogContentText
      {...props}
      ref={ref}
      color={theme.palette.text.primary}
    />
  );
};

DialogContentTextComponent.displayName = 'DialogContentText';

export const DialogContentText = forwardRef(
  DialogContentTextComponent
) as OverridableComponent<DialogContentTextTypeMap>;
